import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DokaIdentityLoginService } from '@doka/identity-login';
import { DokaI18NextService } from '@doka/i18next';
import { SyncUserModel, UsersClient } from 'src/api/services/generated/api-clients';
import { UserInfoService } from '../shared/services/user-info.service';
import { distinctUntilChanged, firstValueFrom, skip } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StartupService {
  private loginService = inject(DokaIdentityLoginService);
  private i18n = inject(DokaI18NextService);
  private usersClient = inject(UsersClient);
  private userInfoService = inject(UserInfoService);
  private router = inject(Router);

  private isUserSynced = false;

  private getShareCodeFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get('shareCode');
  }

  async init(): Promise<void> {
    await this.syncLoggedInUserAsync();

    // The login service re-emits the user after every token refresh, only sync when another user logged in.
    this.loginService.user$
      .pipe(
        distinctUntilChanged((previous, current) => previous?.id === current?.id),
        skip(1),
      )
      .subscribe(() => void this.syncLoggedInUserAsync());
    this.i18n.langChanged.subscribe((lng) => void this.syncPreferredLanguageAsync(lng));
  }

  private async syncLoggedInUserAsync(): Promise<void> {
    const user = this.loginService.currentUser();
    if (!user?.email) {
      return;
    }

    const shareCode = this.getShareCodeFromUrl();

    try {
      await firstValueFrom(
        this.usersClient.syncUser(
          new SyncUserModel({
            email: user.email,
            firstName: user.firstname,
            lastName: user.lastname,
            profilePictureUrl: user.profilePictureUrl ?? null,
            shareCode: shareCode,
          }),
        ),
      );

      this.isUserSynced = true;
      await this.syncPreferredLanguageAsync(this.i18n.selectedLanguage);

      await this.userInfoService.loadCurrentUser();
    } catch (error: unknown) {
      if (error instanceof Object && 'status' in error && error.status === 403) {
        await this.router.navigate(['/forbidden']);
        return;
      }
      throw error;
    }
  }

  // Stores the UI language so notification emails can be sent in the recipient's language.
  private async syncPreferredLanguageAsync(lng: string | null | undefined): Promise<void> {
    if (!lng || !this.isUserSynced) {
      return;
    }

    try {
      await firstValueFrom(this.usersClient.updateCurrentUserLanguage(lng));
    } catch {
      // Best effort: a failed update must not break the app, emails then fall back to the sender's language.
    }
  }
}
